import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Share2, 
  Linkedin, 
  Twitter, 
  MessageCircle, 
  Send, 
  Download,
  Copy,
  Check,
  Users,
  MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const FRONTEND_URL = window.location.origin;

const ShareModal = ({ isOpen, onClose, project, url, title, description }) => {
  const [copied, setCopied] = useState(false);
  const [isExportingPDF, setIsExportingPDF] = useState(false);

  // Support both project object and custom url/title/description
  const shareUrl = url || `${FRONTEND_URL}/public/projects/${project?.id}`;
  
  const shareText = description || (project ? `Check out this ServiceNow opportunity: ${project.title} at ${project.city}, ${project.country}` : title || 'Check out this profile!');

  const trackShare = async (method) => {
    // Only track for projects (not for companies/resources)
    if (!project?.id) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/projects/${project.id}/track-share`, { share_method: method }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Failed to track share:', error);
    }
  };

  const handleLinkedInShare = () => {
    trackShare('linkedin');
    const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
    window.open(linkedinUrl, '_blank', 'width=600,height=600');
  };

  const handleTwitterShare = () => {
    trackShare('twitter');
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(twitterUrl, '_blank', 'width=600,height=600');
  };

  const handleWhatsAppShare = () => {
    trackShare('whatsapp');
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleTelegramShare = () => {
    trackShare('telegram');
    const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`;
    window.open(telegramUrl, '_blank');
  };

  const handleSlackShare = () => {
    trackShare('slack');
    const slackUrl = `https://slack.com/share?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`;
    window.open(slackUrl, '_blank');
  };

  const handleTeamsShare = () => {
    trackShare('teams');
    const teamsUrl = `https://teams.microsoft.com/share?href=${encodeURIComponent(shareUrl)}&msgText=${encodeURIComponent(shareText)}`;
    window.open(teamsUrl, '_blank');
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
      trackShare('copy_link');
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const handleExportPDF = async () => {
    // PDF export only available for projects
    if (!project?.id) {
      toast.error('PDF export not available for this item');
      return;
    }
    
    setIsExportingPDF(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/projects/${project.id}/export-pdf`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${project.project_id}_${project.title.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('PDF downloaded successfully!');
    } catch (error) {
      toast.error('Failed to export PDF');
      console.error('PDF export error:', error);
    } finally {
      setIsExportingPDF(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="w-5 h-5" style={{ color: '#62D84E' }} />
            {title || (project ? 'Share Project' : 'Share')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Copy Link */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              Public Link
            </label>
            <div className="flex gap-2">
              <Input
                value={shareUrl}
                readOnly
                className="flex-1"
              />
              <Button
                onClick={handleCopyLink}
                variant="outline"
                className="shrink-0"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          {/* Social Share Buttons */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              Share on Social Media
            </label>
            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={handleLinkedInShare}
                className="w-full bg-[#0077B5] hover:bg-[#006399] text-white"
              >
                <Linkedin className="w-4 h-4 mr-2" />
                LinkedIn
              </Button>
              
              <Button
                onClick={handleTwitterShare}
                className="w-full bg-black hover:bg-gray-800 text-white"
              >
                <Twitter className="w-4 h-4 mr-2" />
                X (Twitter)
              </Button>
              
              <Button
                onClick={handleWhatsAppShare}
                className="w-full bg-[#25D366] hover:bg-[#1DA851] text-white"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                WhatsApp
              </Button>
              
              <Button
                onClick={handleTelegramShare}
                className="w-full bg-[#0088cc] hover:bg-[#0077b3] text-white"
              >
                <Send className="w-4 h-4 mr-2" />
                Telegram
              </Button>
              
              <Button
                onClick={handleSlackShare}
                className="w-full bg-[#4A154B] hover:bg-[#3d0f3f] text-white"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Slack
              </Button>
              
              <Button
                onClick={handleTeamsShare}
                className="w-full bg-[#5B5FC7] hover:bg-[#4a4fb0] text-white"
              >
                <Users className="w-4 h-4 mr-2" />
                Teams
              </Button>
            </div>
          </div>

          {/* Export PDF - Only for projects */}
          {project?.id && (
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Export
              </label>
              <Button
                onClick={handleExportPDF}
                disabled={isExportingPDF}
                className="w-full btn-snow"
              >
                <Download className="w-4 h-4 mr-2" />
                {isExportingPDF ? 'Generating PDF...' : 'Download as PDF'}
              </Button>
            </div>
          )}

          <p className="text-xs text-gray-500 text-center pt-2">
            Share this project to maximize visibility and attract top talent!
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShareModal;
